import React, { useEffect, useState } from 'react';
import { Button, Badge, Modal, Row, Col, Form, Select, Space, DatePicker, Tooltip } from 'antd';
import { FileExcelOutlined, ClearOutlined } from '@ant-design/icons';
import { FetchApiInterface } from "../../../../../interfaces/FetchApi.interface";
import { AxiosResponse } from "axios";
import { ResponseInterface } from "../../../../../interfaces/Response.interface";
import useFetch from "../../../../../hooks/useFetch";
import useMessageUser from "../../../../../hooks/useMessageUser";
import { USER_INFO } from "../../../../../config/config";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";

const { Option } = Select;

interface FiltersProps {
  openModal: any;
  setOpenModal: Function;
  filterApply: any;
  onFinish: (values: any) => void;
  cleanFilters: () => void;
}

const Filters6: React.FC<FiltersProps> = ({ filterApply, onFinish, cleanFilters, openModal, setOpenModal }) => {
  const [form] = Form.useForm();
  const { fetchApi } = useFetch();
  const [statusOptions, setStatusOptions] = useState([]);
  const [catalogsOptions, setCatalogsOptions] = useState([]);
  const [communityWorkers, setCommunityWorkers] = useState([]);
  const [PUOptions, setPUOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [COMOptions, setCOMOptions] = useState([]);
  const [communitiesOptions, setCommunitiesOptions] = useState([]);
  const [nationalOfficeOptions, setNationalOfficeOptions] = useState([]);
  const [translators, setTranslators] = useState<any[]>([]);
  const [selectedRange, setSelectedRange] = useState<any>([]);
  const { showMessage } = useMessageUser();
  const { t } = useTranslation();

  useEffect(() => {
    if (openModal) {
      getEasyList();
      getCatalosList();
      getStatusList();
      getPuList();
      getCOM();
      getNationalOfficeList();
      getTranslators();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal]);

  const clear = () => {
    form.resetFields();
    form.setFieldsValue({ status: undefined });
    cleanFilters();
  }

  const getEasyList = () => {
    const dataSend: FetchApiInterface = {
      url: `/comms/control/get_facilitador`,
      method: 'GET'
    }
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface>) => {
        if (data.status === 'success') {
          setCommunityWorkers(data?.data || []);
        } else {
          setCommunityWorkers([]);
          showMessage(data?.message, 'info');
        }
      }
    ).catch((reason: any) => {
      setCommunityWorkers([]);
      showMessage(reason?.message, 'error');
    });
  }
  const getCatalosList = () => {
    const dataSend: FetchApiInterface = {
      url: `/comms/control/get_commitment_status`,
      method: 'GET'
    }
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface>) => {
        if (data.status === 'success') {
          setCatalogsOptions(data?.data || []);
        } else {
          setCatalogsOptions([]);
          showMessage(data?.message, 'info');
        }
      }
    ).catch((reason: any) => {
      setCatalogsOptions([]);
      showMessage(reason?.message, 'error');
    });
  }

  const getStatusList = () => {
    const dataSend: FetchApiInterface = {
      url: `/comms/control/get_status_sc`,
      method: 'GET'
    }
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface>) => {
        if (data.status === 'success') {
          setStatusOptions(data?.data || []);
        } else {
          showMessage(data?.message, 'info');
        }
      }
    ).catch((reason: any) => {
      showMessage(reason?.message, 'error');
    });
  }
  const getCOM = () => {
    const dataSend: FetchApiInterface = {
      url: `/comms/control/get_com_type`,
      method: 'GET'
    }
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface>) => {
        if (data.status === 'success') {
          setCOMOptions(data?.data || []);
        } else {
          showMessage(data?.message, 'info');
        }
      }
    ).catch((reason: any) => {
      showMessage(reason?.message, 'error');
    });
  }
  const getPuList = () => {
    const dataSend: FetchApiInterface = {
      url: `/comms/control/getPuandCountry`,
      method: 'GET'
    }
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface | any>) => {
        // El endpoint devuelve el arreglo directo; se contempla tambien la
        // respuesta envuelta en { status, data } por si el backend la unifica.
        const list = Array.isArray(data) ? data : (data?.status === 'success' ? data?.data : null);
        if (list) {
          setPUOptions(list || []);
        } else {
          setPUOptions([]);
          showMessage(data?.message, 'info');
        }
      }
    ).catch((reason: any) => {
      showMessage(reason?.message, 'error');
    });
  }
  const getNationalOfficeList = () => {
    const dataSend: FetchApiInterface = {
      url: `/comms/control/get_national_office`,
      method: 'GET'
    }
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface>) => {
        if (data.status === 'success') {
          setNationalOfficeOptions(data?.data || []);
        } else {
          showMessage(data?.message, 'info');
        }
      }
    ).catch((reason: any) => {
      showMessage(reason?.message, 'error');
    });
  }

  const getTranslators = () => {
    const userInfo = JSON.parse(localStorage.getItem(USER_INFO) || '{}');
    const dpaIdCountry = userInfo?.user?.dpa?.dpaIdCountry;
    const countryParam = dpaIdCountry ? `dpaCountryId=${dpaIdCountry}` : '';
    const base: FetchApiInterface = { url: `/users/get-translators${countryParam ? `?${countryParam}` : ''}`, method: 'GET' };
    const all: FetchApiInterface = { url: `/users/get-translators?all=true${countryParam ? `&${countryParam}` : ''}`, method: 'GET' };
    // Cada peticion se resuelve por separado: si una falla devuelve null y la
    // lista se arma igual con lo que haya respondido la otra.
    let ultimoError: any = null;
    const pedir = (config: FetchApiInterface): Promise<any[] | null> =>
      fetchApi(config)
        .then(({ data }: AxiosResponse<ResponseInterface>) => (
          data?.status === 'success' ? data?.data || [] : []
        ))
        .catch((reason: any) => {
          ultimoError = reason;
          return null;
        });

    Promise.all([pedir(base), pedir(all)]).then(([list1, list2]) => {
      const merged = [...(list1 || []), ...(list2 || [])];
      const unique = merged.filter((t, i, arr) => arr.findIndex(x => x.useId === t.useId) === i);
      setTranslators(unique);
      // Solo se avisa cuando fallaron las dos, es decir cuando no hay lista.
      if (list1 === null && list2 === null) {
        showMessage(ultimoError?.message, 'error');
      }
    });
  }

  const getCommunitiesCodes = (puCode: number) => {
    form.setFieldsValue({ community: null });
    setCommunitiesOptions([]);
    const dataSend: FetchApiInterface = {
      url: `/comms/control/get_communities/${puCode}`,
      method: 'GET'
    }
    fetchApi(dataSend).then(
      ({ data }: AxiosResponse<ResponseInterface>) => {
        if (data.status === 'success') {
          setCommunitiesOptions(data?.data || []);
        } else {
          showMessage(data?.message, 'info');
        }
      }
    ).catch((reason: any) => {
      showMessage(reason?.message, 'error');
    });
  }

  const handleSegmentClick = (rangeType: any) => {
    const today = moment();
    let startDate;

    switch (rangeType) {
      case '24h':
        startDate = today.clone().subtract(24, 'hours');
        break;
      case '7d':
        startDate = today.clone().subtract(7, 'days');
        break;
      case '1m':
        startDate = today.clone().subtract(1, 'months');
        break;
      case '3m':
        startDate = today.clone().subtract(3, 'months');
        break;
      case '6m':
        startDate = today.clone().subtract(6, 'months');
        break;
      case '1y':
        startDate = today.clone().subtract(1, 'years');
        break;
      default:
        startDate = today;
    }
    setSelectedRange([startDate, today]);
    form.setFieldsValue({ read: { value1: startDate, value2: today } });
  };

  return (
    <Col>
      <Space>
        <Badge dot={(Object.keys(filterApply)?.length > 0)}>
          <Button type='primary' ghost icon={<FileExcelOutlined />} onClick={() => setOpenModal(true)}>
            {t('filters')}
          </Button>
        </Badge>
        <Tooltip title={t('clearFilters')}>
          <Button type='primary' danger ghost icon={<ClearOutlined />} onClick={clear} />
        </Tooltip>
      </Space>
      <Modal centered width={860} title={t('filters')} open={openModal} onCancel={() => setOpenModal(false)} footer={
        <Row justify='space-between'>
          <Col>
            <Button shape='round' type='primary' danger ghost onClick={clear}>{t('clearFilters')}</Button>
          </Col>
          <Col>
            <Space>
              <Button shape='round' type='text' onClick={() => setOpenModal(false)}>{t('cancel')}</Button>
              <Button shape='round' type='primary' onClick={() => form.submit()}>{t('applyFilters')}</Button>
            </Space>
          </Col>
        </Row>
      }>
        <Row>
          <Col span={24}>
            <Form layout='vertical' form={form} onFinish={onFinish} initialValues={{ status: ['PENDING', 'OVERDUE_PENDING', 'PENDING_RETURN'] }}>
              <Row gutter={[10, 0]}>
                <Col span={24} md={12}>
                  <Form.Item name='assComType' label={t('com')}>
                    <Select mode='multiple' allowClear showSearch placeholder={t('selectOption')}>
                      {
                        COMOptions.map((option: any) => (
                          <Option key={option} value={option}>
                            {option}
                          </Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24} md={12}>
                  <Form.Item name='status' label={t('letterStatus')}>
                    <Select mode='multiple' allowClear showSearch placeholder={t('selectOption')}>
                      {
                        [...catalogsOptions].sort((a: any, b: any) => t(a).localeCompare(t(b))).map((option: any) => (
                          <Option key={option} value={option}>
                            {t(option)}
                          </Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Row gutter={[10, 0]}>
                    <Col span={24} md={12}>
                      <Form.Item name='assNo' label={t('nationalOffice')}>
                        <Select allowClear showSearch placeholder={t('selectOption')}>
                          {
                            nationalOfficeOptions.map((option: any, index) => (
                              <Option key={index} value={option}>{option}</Option>
                            ))
                          }
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={24} md={12}>
                      <Form.Item name='ascCw' label={t('facilitator')}>
                        <Select allowClear showSearch placeholder={t('selectOption')}>
                          {
                            communityWorkers.map((worker: any, index) => (
                              <Option key={index} value={worker}>{worker}</Option>
                            ))
                          }
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <p><strong>{t('place')}</strong></p>
                  <Row gutter={[10, 0]}>
                    <Col span={24} md={12}>
                      <Form.Item name='ascPuCode' label='PU'>
                        <Select
                          allowClear
                          showSearch
                          optionFilterProp='label'
                          options={PUOptions}
                          placeholder={t('selectPu')}
                          onChange={getCommunitiesCodes}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24} md={12}>
                      <Form.Item name='ascLevel' label={t('community')}>
                        <Select disabled={(communitiesOptions.length <= 0)} allowClear showSearch placeholder={t('selectCommunity')}>
                          {
                            communitiesOptions.map((option: any) => (
                              <Option key={option} value={option}>
                                {option}
                              </Option>
                            ))
                          }
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
                <Col span={24}>
                  <Row gutter={[10, 0]}>
                    <Col span={24}>
                      <p><strong>{t('receivedUa')}</strong></p>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['read', 'value1']} label={t('from')}>
                        <DatePicker
                          format="DD-MM-YY"
                          style={{ width: '100%' }}
                          placeholder="dd/mm/aa"
                          value={selectedRange[0]}
                          onChange={(date) => setSelectedRange([date, selectedRange[1]])}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['read', 'value2']} label={t('to')}>
                        <DatePicker
                          format="DD-MM-YYYY"
                          style={{ width: '100%' }}
                          placeholder="dd/mm/aa"
                          value={selectedRange[1]}
                          onChange={(date) => setSelectedRange([selectedRange[0], date])}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <p><strong>{t('showLast')}</strong></p>
                      <Button onClick={() => handleSegmentClick('24h')}>24 h</Button>
                      <Button onClick={() => handleSegmentClick('7d')}>7 {t('days')}</Button>
                      <Button onClick={() => handleSegmentClick('1m')}>1 {t('month')}</Button>
                      <Button onClick={() => handleSegmentClick('3m')}>3 {t('months')}</Button>
                      <Button onClick={() => handleSegmentClick('6m')}>6 {t('months')}</Button>
                      <Button onClick={() => handleSegmentClick('1y')}>1 {t('year')}</Button>
                    </Col>
                  </Row>
                </Col>
                <Col span={24} md={12}>
                  <Form.Item name='statusSc' label={t('scStatus')}>
                    <Select mode='multiple' allowClear showSearch placeholder={t('selectOption')}>
                      {
                        statusOptions.map((option: any) => (
                          <Option key={option} value={option}>
                            {option}
                          </Option>
                        ))
                      }
                    </Select>
                  </Form.Item>
                </Col>

                {/* Nuevos filtros de traducción */}
                <Col span={24}>
                  <br />
                  <p><strong>{t('translation')}</strong></p>
                  <Row gutter={[10, 0]}>
                    <Col span={24} md={12}>
                      <Form.Item name='assignedTranslatorId' label={t('translator')}>
                        <Select mode='multiple' allowClear showSearch placeholder={t('selectTranslator')}
                          filterOption={(input, option) =>
                            String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                          }>
                          {translators.map((t: any) => (
                            <Option key={t.useId} value={t.useId}>
                              {`${t.useName} ${t.useLastName}`}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    {/* <Col span={24} md={12}>
                      <Form.Item name='translationDirection' label='Flujo traducción'>
                        <Select mode='multiple' allowClear placeholder='Seleccione flujo'>
                          <Option value='SC_TO_SP'>SC-SP</Option>
                          <Option value='SP_TO_SC'>SP-SC</Option>
                        </Select>
                      </Form.Item>
                    </Col> */}
                    <Col span={24}>
                      <Row gutter={[10, 0]}>
                        <Col span={24} md={12}>
                          <Form.Item name='translationRequestedAt' label={t('from')}>
                            <DatePicker format='DD/MM/YYYY' style={{ width: '100%' }} placeholder='dd/mm/aaaa' />
                          </Form.Item>
                        </Col>
                        <Col span={24} md={12}>
                          <Form.Item name='translationDueDate' label={t('to')}>
                            <DatePicker format='DD/MM/YYYY' style={{ width: '100%' }} placeholder='dd/mm/aaaa' />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>
    </Col>
  );
};

export default Filters6;
